import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import isEmpty from 'lodash/isEmpty';
import moment from 'moment';
import { Auth, signOut } from '@firebase/auth';
import { FirebaseApp } from '@firebase/app';
import { API_CONFIG, redirectionAppUrl } from 'shared/constants/constant';
import { IProfileData, IUserData, IUserSetting } from 'shared/interface';
import AuthService from 'shared/services/auth.service';
import HttpService from 'shared/services/http.service';
import { useFirebase } from 'shared/firebase';
import { convertTimestamp, convertToSeconds, ISoundKey, playSound, soundToggle } from 'shared/utility';
import useModal from 'shared/hooks/useModal';
import { ModalType, TrackingType } from 'shared/constants/enum';
import JokerShopModal from '../modals/jokerShopModal';
import { useTranslation } from 'react-i18next';
import { GAME_PROPERTY_KEY } from 'pixiLib/Constants';
import GameData from 'pixiLib/common/GameData';
import { notify } from '../notification/notification';
import { Languages } from 'shared/util/localization';
import localizationConstants from 'shared/util/translation/constant';
import Spinner from '../spinner/spinner';
import { useQueryParams } from './queryParamContext';

export interface IPurchaseGameDataProps {
	bookTimeStamp: number;
	subType: string;
	type: string;
	uniqueId: string;
}

export type DataType = {
	[key: string]: IPurchaseGameDataProps;
};

interface IUserContext {
	app: FirebaseApp;
	auth: Auth;
	isFirebaseLoading: boolean;
	userData: IUserData;
	onLogin: (token: string, userData: IUserData, isUpdateToken?: boolean) => void;
	logout: () => void;
	getUserProfile: () => void;
	userProfileData: IProfileData;
	setUserProfileData: (profileData: IProfileData) => void;
	setSpellJoker: (spellJoker: any) => void;
	spellJoker: any;
	settingData: IUserSetting;
	timeLeft: number;
	isCollect: boolean;
	isButtonLoading: boolean;
	nextCollectTime: number;
	collectFreeJoker: () => void;
	getFreeJoker: () => void;
	closeModal: () => void;
	isOpen: boolean;
	openModal: (type: string) => void;
	type: unknown;
	language: string;
	setLanguage: (language: Languages) => void;
	isSound: boolean;
	setIsSound: (isSOund: boolean) => void;
	token: string;
	setToken: (token: string) => void;
	isSettingLoading: boolean;
	purchaseGameList: DataType;
	isPurchaseGameLoading: boolean;
	purchaseGame: (payload: any) => void;
	handleTracking: (trackingName: string, type?: string | '') => void;
}

const UserContext = createContext<IUserContext | undefined>(undefined);

export const useUserContext = () => {
	const context = useContext(UserContext);
	if (!context) {
		throw new Error('useUserContext must be used within UserProvider');
	}
	return context;
};

export const UserProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
	const { app, auth, isFirebaseLoading, handleTracking } = useFirebase();

	const [userData, setUserData] = useState(AuthService.getUserData());
	const [token, setToken] = useState('');
	const [userProfileData, setUserProfileData] = useState({} as IProfileData);
	const [spellJoker, setSpellJoker] = useState({});
	const [settingData, setSettingData] = useState({} as IUserSetting);
	const [timeLeft, setTimeLeft] = useState(0);
	const [isCollect, setIsCollect] = useState(false);
	const [isButtonLoading, setIsButtonLoading] = useState(false);
	const [isSettingLoading, setIsSettingLoading] = useState(false);
	const [nextCollectTime, setNextCollectTime] = useState(0);
	const [language, setLanguage] = useState<Languages>(Languages.DE);
	const [isSound, setIsSound] = useState(false);

	const [purchaseGameList, setPurchaseGameList] = useState<DataType>({});
	const [isPurchaseGameLoading, setIsPurchaseGameLoading] = useState(true);

	const { closeModal, isOpen, openModal, type } = useModal();
	const { i18n } = useTranslation();

	const { queryParams, setSearchParams } = useQueryParams();

	const getPurchaseGame = useCallback(async () => {
		await HttpService.get(`${API_CONFIG.path.consumableJoker}/unique`)
			.then((data) => {
				setPurchaseGameList(data);
				setIsPurchaseGameLoading(false);
			})
			.catch((error) => {
				console.error('error', error);
				setIsPurchaseGameLoading(false);
			});
	}, []);

	const purchaseGame = useCallback(async (payload: any) => {
		await HttpService.post(`${API_CONFIG.path.consumableJoker}/unique`, payload)
			.then((res) => {
				setPurchaseGameList((data) => ({ ...data, [res.uniqueId]: res }));
				notify(i18n.t(localizationConstants.successfullyPurchase), 'success');
			})
			.catch((error) => {
				console.error('error', error);
			});
	}, []);

	const fetchUserSettings = useCallback(async () => {
		const savedLanguage = localStorage.getItem('language');
	}, [i18n]);

	const getGameSettings = useCallback(async () => {
		setIsSettingLoading(false);
	}, []);

	const getUserProfile = useCallback(async () => {
		try {
			const result = await HttpService.get(`${API_CONFIG.path.user}/info`);
			setUserProfileData(result);
		} catch (error) {
			console.error('error', error);
		}
	}, []);

	const getUserJokerCount = useCallback(async () => {
		try {
			const result = await HttpService.get(`${API_CONFIG.path.consumableJoker}/consumables`);
			setSpellJoker(result);
		} catch (error) {
			console.error('error', error);
		}
	}, []);

	const onLogin = useCallback((token: string, userData: IUserData, isUpdateToken = false) => {
		setToken(token);
		AuthService.setAuthData(token);
		AuthService.setUserData(userData);
		if (!isUpdateToken) {
			setUserData(userData as IUserData);
		}
	}, []);

	const logout = useCallback(() => {
		const rateFuchUrl = `${redirectionAppUrl}/logout${queryParams ? `?vendor=${queryParams}` : ''}`;

		// const destination = queryParams
		// 	? rateFuchUrl.includes('?')
		// 		? `${rateFuchUrl}&vendor=${queryParams}`
		// 		: `${rateFuchUrl}?vendor=${queryParams}`
		// 	: rateFuchUrl;
		window.location.href = rateFuchUrl;
		if (!isEmpty(auth)) {
			signOut(auth);
		}
		AuthService.logout();
		AuthService.removeAuthData();
		AuthService.setUserData({} as IUserData);
		setTimeout(() => {
			setUserData({} as IUserData);
			setUserProfileData({} as IProfileData);
			setIsSound(false);
		}, 500);
	}, [auth, queryParams]);

	const getFreeJoker = useCallback(async () => {
		const currentTime = moment().unix();
		try {
			await HttpService.get(`${API_CONFIG.path.user}/timeReward`)
				.then((data) => {
					setNextCollectTime(data.nextCollectTime);
					const convertTime = convertTimestamp(data.nextCollectTime - currentTime);
					const actualTimeLeft =
						convertToSeconds(convertTime) - convertToSeconds(moment().format('HH:mm:ss'));
					actualTimeLeft > 0 && setTimeLeft(actualTimeLeft);
					if (actualTimeLeft > 0) {
						setIsCollect(true);
					}
				})
				.catch((error) => {
					console.error('error', error);
				});
		} catch (error) {
			console.error('error:', error);
		}
	}, []);

	const collectFreeJoker = async () => {
		playSound(ISoundKey.gameSound);
		const currentTime = moment().unix();
		setIsButtonLoading(true);
		try {
			await HttpService.post(`${API_CONFIG.path.user}/timeReward/collect`)
				.then((data) => {
					setIsCollect(true);
					setNextCollectTime(data.timeRewardData.nextCollectTime);
					const convertTime = convertTimestamp(data.timeRewardData.nextCollectTime - currentTime);
					const actualTimeLeft =
						convertToSeconds(convertTime) - convertToSeconds(moment().format('HH:mm:ss'));
					setTimeLeft(actualTimeLeft);
					getFreeJoker();
					setSpellJoker(data?.userInvConsumables);
					handleTracking(TrackingType.CollectTimeBonus);
					setIsButtonLoading(false);
				})
				.catch((error) => {
					console.error('error', error);
					setIsCollect(false);
					setIsButtonLoading(false);
				});
		} catch (error) {
			console.error('error:', error);
			setIsCollect(true);
			setIsButtonLoading(false);
		}
	};

	useEffect(() => {
		getGameSettings();
	}, [getGameSettings]);

	useEffect(() => {
		if (!isEmpty(userData) && token !== '') {
			getUserJokerCount();
			fetchUserSettings();
			getPurchaseGame();
			getUserProfile();
		}
	}, [fetchUserSettings, getPurchaseGame, getUserJokerCount, getUserProfile, userData, token]);

	useEffect(() => {
		let timerId: number;
		if (timeLeft > 0) {
			timerId = window.setInterval(() => {
				setTimeLeft((prevTime: number) => prevTime - 1);
			}, 1000);
			setIsCollect(true);
		} else {
			setIsCollect(false);
		}

		return () => window.clearInterval(timerId);
	}, [setIsCollect, setTimeLeft, timeLeft]);

	useEffect(() => {
		if (settingData.vendorData && !(queryParams && settingData.vendorData[queryParams])) {
			setSearchParams((pre) => {
				pre.delete('vendor');
				return pre;
			});
		}
	}, [queryParams, setSearchParams, settingData]);

	return (
		<UserContext.Provider
			value={{
				app,
				auth,
				isFirebaseLoading,
				isPurchaseGameLoading,
				purchaseGame,
				purchaseGameList,
				userData,
				onLogin,
				logout,
				getUserProfile,
				userProfileData,
				setUserProfileData,
				setSpellJoker,
				spellJoker,
				settingData,
				timeLeft,
				nextCollectTime,
				isCollect,
				isButtonLoading,
				collectFreeJoker,
				getFreeJoker,
				closeModal,
				isOpen,
				openModal,
				type,
				language,
				setLanguage,
				isSound,
				setIsSound,
				token,
				setToken,
				isSettingLoading,
				handleTracking
			}}
		>
			{/* {isFirebaseLoading && <Spinner />} */}
			{children}
			{/* {!isFirebaseLoading && (
				<>
					
					{isOpen && type === ModalType.JokerShopModal && (
						<JokerShopModal
							settingData={settingData}
							collectFreeJoker={collectFreeJoker}
							isCollect={isCollect}
							isLoading={isButtonLoading}
							timeLeft={timeLeft}
							getFreeJoker={getFreeJoker}
							openPromoCodeModal={(type: string) => openModal(type)}
						/>
					)}
				</>
			)} */}
		</UserContext.Provider>
	);
};
